import {
  EmployeeFormContext,
  IEmployeeFormActions,
  IEmployeeFormSubmissionCommentAttachmentAddActionPayload,
  IEmployeeFormSubmissionCommentAttachmentRemoveActionPayload,
  IEmployeeFormSubmissionCommentFetchActionPayload,
  IEmployeeFormSubmissionCommentUpdateActionPayload,
  IEmployeeFormSubmissionFetchActionPayload
} from './actions.types';
import {
  IDepartmentData,
  IEmployeeFormSubmissionChecklistData,
  IEmployeeFormSubmissionCommentData,
  IEmployeeFormSubmissionData,
  IUserData
} from '@/models';
import { ITransition } from '@/models/transition';
import {
  IEmployeeFormPaginatedListData,
  IUpdateSubmissionChecklistAssigneePayload,
  IUpdateEmployeeFormSubmissionStatePayload
} from '@/services';

export const actions: IEmployeeFormActions = {
  fetchEmployeeFormSubmissions(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.employeeForm.fetchEmployeeFormSubmissions(
      payload
    );
    context.commit('SET_LAST_PROMISE', promise);

    return promise.then((paginated: IEmployeeFormPaginatedListData) => {
      if (promise == context.state.lastPromise) {
        context.commit('SET_COUNT', paginated.count);
        context.commit(
          'SET_EMPLOYEE_FORM_SUBMISSIONS',
          paginated.results as IEmployeeFormSubmissionData[]
        );
      }
      context.commit('SET_LAST_PROMISE', null);
    });
  },

  fetchEmployeeFormSubmission(
    context: EmployeeFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmission(payload)
      .then((employeeForm: IEmployeeFormSubmissionData) => {
        const index = context.state.employeeFormSubmissions.findIndex(
          (v: IEmployeeFormSubmissionData) => v.id === employeeForm.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_SUBMISSION', employeeForm);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION', employeeForm);
        }
      });
  },

  updateEmployeeFormSubmissionState(
    context: EmployeeFormContext,
    payload: IUpdateEmployeeFormSubmissionStatePayload
  ): Promise<void> {
    return this.$services.employeeForm
      .updateEmployeeFormSubmissionState(payload)
      .then((employeeForm: IEmployeeFormSubmissionData) => {
        const index = context.state.employeeFormSubmissions.findIndex(
          (v: IEmployeeFormSubmissionData) => v.id === employeeForm.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_SUBMISSION', employeeForm);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION', employeeForm);
        }
      });
  },

  createEmployeeFormSubmission(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionData
  ): Promise<number> {
    return this.$services.employeeForm
      .createEmployeeFormSubmission(payload)
      .then((employeeForm: IEmployeeFormSubmissionData) => {
        context.commit('ADD_EMPLOYEE_FORM_SUBMISSION', employeeForm);
        context.commit('INCREMENT_COUNT');

        return employeeForm.id!;
      });
  },

  updateEmployeeFormSubmission(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionData
  ): Promise<void> {
    return this.$services.employeeForm
      .updateEmployeeFormSubmission(payload)
      .then((employeeForm: IEmployeeFormSubmissionData) => {
        const index = context.state.employeeFormSubmissions.findIndex(
          (v: IEmployeeFormSubmissionData) => v.id === employeeForm.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_SUBMISSION', employeeForm);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION', employeeForm);
        }
      });
  },

  deleteEmployeeFormSubmission(
    context: EmployeeFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.employeeForm
      .deleteEmployeeFormSubmission(payload)
      .then(() => {
        context.commit('REMOVE_EMPLOYEE_FORM_SUBMISSION', payload);
        context.commit('DECREMENT_COUNT');
      });
  },

  exportEmployeeFormSubmissionPDF(
    _context: EmployeeFormContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.employeeForm.exportEmployeeFormSubmissionPDF(payload);
  },

  fetchEmployeeFormSubmissionChecklists(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionData
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmissionChecklists(payload)
      .then((checklist: IEmployeeFormSubmissionChecklistData[]) => {
        context.commit('SET_EMPLOYEE_FORM_CHECKLISTS', checklist);
      });
  },

  updateEmployeeFormSubmissionChecklist(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionChecklistData
  ): Promise<void> {
    return this.$services.employeeForm
      .updateEmployeeFormSubmissionChecklist(payload)
      .then((checklist: IEmployeeFormSubmissionChecklistData) => {
        const index = context.state.employeeFormChecklists.findIndex(
          (v: IEmployeeFormSubmissionChecklistData) => v.id === checklist.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_CHECKLIST', checklist);
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_CHECKLIST', checklist);
        }
      });
  },

  markCompleteEmployeeFormSubmissionChecklist(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionChecklistData
  ): Promise<void> {
    return this.$services.employeeForm
      .markCompleteEmployeeFormSubmissionChecklist(payload)
      .then((checklist: IEmployeeFormSubmissionChecklistData) => {
        const index = context.state.employeeFormChecklists.findIndex(
          (v: IEmployeeFormSubmissionChecklistData) => v.id === checklist.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_CHECKLIST', checklist);
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_CHECKLIST', checklist);
        }
      });
  },

  updateEmployeeFormSubmissionChecklistAssignee(
    context: EmployeeFormContext,
    payload: IUpdateSubmissionChecklistAssigneePayload
  ): Promise<void> {
    return this.$services.employeeForm
      .updateEmployeeFormSubmissionChecklistAssignee(payload)
      .then((checklist: IEmployeeFormSubmissionChecklistData) => {
        const index = context.state.employeeFormChecklists.findIndex(
          (v: IEmployeeFormSubmissionChecklistData) => v.id === checklist.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_CHECKLIST', checklist);
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_CHECKLIST', checklist);
        }
      });
  },

  fetchChecklistAssigneeOptions(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionChecklistData
  ): Promise<IUserData[]> {
    return this.$services.employeeForm.fetchChecklistAssigneeOptions(payload);
  },

  fetchEmployeeFormSubmissionDepartments(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionData
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmissionDepartments(payload)
      .then((departments: IDepartmentData[]) => {
        context.commit('SET_EMPLOYEE_FORM_DEPARTMENTS', departments);
      });
  },

  //comment
  fetchEmployeeFormSubmissionComments(
    context: EmployeeFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmissionComments(payload)
      .then((comments: IEmployeeFormSubmissionCommentData[]) => {
        context.commit('SET_EMPLOYEE_FORM_SUBMISSION_COMMENTS', comments);
      });
  },

  fetchEmployeeFormSubmissionComment(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmissionComment(
        payload.submissionId,
        payload.commentId
      )
      .then((comment: IEmployeeFormSubmissionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  createEmployeeFormSubmissionComment(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.employeeForm
      .createEmployeeFormSubmissionComment(
        payload.submissionId,
        payload.comment
      )
      .then((comment: IEmployeeFormSubmissionCommentData) => {
        context.commit('SET_LAST_CREATED_COMMENT_ID', comment.id);
        context.commit('ADD_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
      });
  },

  updateEmployeeFormSubmissionComment(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.employeeForm
      .updateEmployeeFormSubmissionComment(
        payload.submissionId,
        payload.comment
      )
      .then((comment: IEmployeeFormSubmissionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  deleteEmployeeFormSubmissionComment(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.employeeForm
      .deleteEmployeeFormSubmissionComment(
        payload.submissionId,
        payload.commentId
      )
      .then(() => {
        context.commit(
          'REMOVE_EMPLOYEE_FORM_SUBMISSION_COMMENT',
          payload.commentId
        );
      });
  },

  addEmployeeFormSubmissionCommentAttachments(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.employeeForm
      .addEmployeeFormSubmissionCommentAttachments(
        payload.submissionId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IEmployeeFormSubmissionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        } else {
          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  removeEmployeeFormSubmissionCommentAttachment(
    context: EmployeeFormContext,
    payload: IEmployeeFormSubmissionCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.employeeForm
      .removeEmployeeFormSubmissionCommentAttachment(
        payload.submissionId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_EMPLOYEE_FORM_SUBMISSION_COMMENT', comment);
        }
      });
  },

  // ACTIVITES
  fetchEmployeeFormSubmissionActivities(
    context: EmployeeFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmissionActivities(payload)
      .then((activities: ITransition[]) => {
        context.commit('SET_EMPLOYEE_FORM_SUBMISSION_ACTIVITIES', activities);
      });
  },

  // Mentions
  fetchEmployeeFormSubmissionCommentsMentionableUsers(
    context: EmployeeFormContext,
    payload: number
  ): Promise<void> {
    return this.$services.employeeForm
      .fetchEmployeeFormSubmissionCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_EMPLOYEE_FORM_MENTIONABLE_USERS', mentionableUsers);
      });
  }
};
